import React from "react";
import { TATVA_PHOTO_B64 } from "../lib/tatvaPhoto";

/**
 * 2-page A4 Resume for the Marisa Peer / More Than Enough
 * "AI-Powered Social Media & Content Systems Manager" application.
 *
 * Aesthetic: editorial cream + deep ink + bronze + sage — magazine spread.
 * Print: A4 (794 × 1123 @ 96dpi). Use browser Print → "Save as PDF" → A4.
 */

const CREAM = "#f4ede0";
const CREAM_DEEP = "#ebe0cd";
const INK = "#1a1a1a";
const INK_SOFT = "#4a4338";
const BRONZE = "#8a6f47";
const SAGE = "#8c9c80";
const RULE = "rgba(26,26,26,0.18)";
const RULE_SOFT = "rgba(26,26,26,0.10)";

const PHOTO_URL = TATVA_PHOTO_B64;

const SERIF = '"Playfair Display", Georgia, "Times New Roman", serif';
const SANS = "Inter, -apple-system, BlinkMacSystemFont, sans-serif";

/* ─────────── small atoms ─────────── */

const Eyebrow = ({ children, color = BRONZE }) => (
    <p
        style={{
            fontFamily: SANS,
            color,
            fontSize: 9,
            letterSpacing: "0.36em",
            textTransform: "uppercase",
            fontWeight: 600,
            margin: 0,
        }}
    >
        {children}
    </p>
);

const SectionLabel = ({ num, label }) => (
    <div style={{ marginBottom: 14, display: "flex", alignItems: "center", gap: 12 }}>
        <span
            style={{
                fontFamily: SERIF,
                color: BRONZE,
                fontSize: 14,
                fontStyle: "italic",
                fontWeight: 500,
            }}
        >
            {num}
        </span>
        <span
            style={{
                flex: 1,
                height: 1,
                backgroundColor: RULE,
            }}
        />
        <span
            style={{
                fontFamily: SANS,
                color: INK,
                fontSize: 10,
                letterSpacing: "0.36em",
                textTransform: "uppercase",
                fontWeight: 700,
            }}
        >
            {label}
        </span>
    </div>
);

const Page = ({ children, testid }) => (
    <article
        className="resume-page"
        data-testid={testid}
        style={{
            width: 794,
            height: 1120,
            maxWidth: "100%",
            margin: "0 auto 24px",
            backgroundColor: CREAM,
            color: INK,
            boxShadow: "0 24px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(26,26,26,0.06)",
            padding: "44px 60px 32px",
            boxSizing: "border-box",
            position: "relative",
            fontFamily: SANS,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
        }}
    >
        {children}
    </article>
);

const Bullet = ({ children }) => (
    <li
        style={{
            color: INK_SOFT,
            fontSize: 9.8,
            lineHeight: 1.5,
            fontWeight: 400,
            marginBottom: 3,
            paddingLeft: 16,
            position: "relative",
            listStyle: "none",
        }}
    >
        <span
            style={{
                position: "absolute",
                left: 0,
                top: 8,
                width: 6,
                height: 1,
                backgroundColor: BRONZE,
            }}
        />
        {children}
    </li>
);

/* ─────────── PAGE 1 ─────────── */

const PageOne = ({ portable = false }) => (
    <Page testid="resume-page-1">
        {/* Header */}
        <header
            style={{
                display: "grid",
                gridTemplateColumns: "180px 1fr",
                gap: 28,
                alignItems: "flex-start",
                marginBottom: 36,
            }}
        >
            {/* Photo column */}
            <div
                style={{
                    width: 180,
                    height: 220,
                    backgroundColor: CREAM_DEEP,
                    border: `1px solid ${RULE}`,
                    position: "relative",
                    overflow: "hidden",
                }}
                data-testid="resume-photo"
            >
                <img
                    src={PHOTO_URL}
                    alt="Tatva K"
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "center 30%",
                        display: "block",
                    }}
                />
                <div
                    style={{
                        position: "absolute",
                        bottom: -8,
                        left: -8,
                        width: 56,
                        height: 56,
                        border: `1px solid ${BRONZE}`,
                        pointerEvents: "none",
                    }}
                />
            </div>

            {/* Name + title column */}
            <div style={{ paddingTop: 6 }}>
                <Eyebrow>The Reset Sanctuary · Dubai, UAE</Eyebrow>
                <h1
                    style={{
                        fontFamily: SERIF,
                        fontSize: 64,
                        fontWeight: 500,
                        lineHeight: 0.98,
                        letterSpacing: "-0.02em",
                        color: INK,
                        margin: "14px 0 12px",
                    }}
                >
                    Tatva K
                </h1>
                <p
                    style={{
                        fontFamily: SERIF,
                        fontStyle: "italic",
                        fontSize: 17,
                        color: BRONZE,
                        margin: "0 0 18px",
                        lineHeight: 1.4,
                        fontWeight: 400,
                    }}
                >
                    AI Systems Architect &middot; Audio-Visual Producer &middot;
                    Direct-Response Strategist
                </p>
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "4px 22px",
                        fontFamily: SANS,
                        fontSize: 9.5,
                        color: INK_SOFT,
                        letterSpacing: "0.04em",
                    }}
                >
                    <span>tatvak@theresetsanctuary.org</span>
                    <span>theresetsanctuary.org</span>
                    <span>+971 50 676 9007</span>
                    <span>insighttimer.com/resetsanctuary</span>
                    <span>instagram.com/tatvakofficial</span>
                    <span>Dubai, UAE</span>
                </div>
            </div>
        </header>

        {/* Statement */}
        <section style={{ marginBottom: 28 }}>
            <SectionLabel num="01" label="Statement" />
            <p
                style={{
                    fontFamily: SERIF,
                    fontSize: 19,
                    lineHeight: 1.45,
                    color: INK,
                    fontWeight: 400,
                    margin: 0,
                    letterSpacing: "-0.005em",
                }}
            >
                I build <em style={{ color: BRONZE }}>AI-powered content engines</em>{" "}
                that turn attention into revenue. Twenty years of audio-visual production,
                a Berklee credential, 150M+ verified organic streams, 4,400+ hours of live
                sound therapy practice, and a full-stack direct-response system &mdash;
                FastAPI, Meta CAPI, Stripe, MailerLite &mdash; that I architected and
                wrote myself. I don&rsquo;t manage channels. I build systems.
            </p>
        </section>

        {/* Two-column band: Why this role / Quick numbers */}
        <section
            style={{
                marginBottom: 28,
                display: "grid",
                gridTemplateColumns: "1.35fr 1fr",
                gap: 28,
            }}
        >
            <div>
                <SectionLabel num="02" label={portable ? "Strengths" : "Fit for More Than Enough"} />
                <ul style={{ margin: 0, padding: 0 }}>
                    <Bullet>
                        Build content systems that tie creative output directly to revenue
                        &mdash; not just engagement.
                    </Bullet>
                    <Bullet>
                        4,400+ hours of live sound practice and Insight Timer Teacher work
                        inform the standard behind every asset I produce.
                    </Bullet>
                    <Bullet>
                        Daily hands-on with the AI stack the role calls for &mdash; Claude,
                        Kaiber, ElevenLabs, Final Cut Pro X, Meta Glasses Gen 2.
                    </Bullet>
                    <Bullet>
                        Dubai-based and ready to start full-time.
                    </Bullet>
                </ul>
            </div>

            {/* Numbers panel */}
            <div
                style={{
                    backgroundColor: CREAM_DEEP,
                    border: `1px solid ${RULE_SOFT}`,
                    padding: "20px 22px",
                    position: "relative",
                }}
            >
                <Eyebrow color={SAGE}>By the numbers</Eyebrow>
                <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 12 }}>
                    {[
                        { v: "150M+", l: "Verified catalog streams" },
                        { v: "385K+", l: "YouTube subscribers (3 properties)" },
                        { v: "130K+", l: "Instagram followers" },
                        { v: "4,400+", l: "Hours live sound practice" },
                        { v: "100%", l: "Meta CAPI event match quality" },
                    ].map((s) => (
                        <div key={s.l} style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                            <span
                                style={{
                                    fontFamily: SERIF,
                                    fontSize: 22,
                                    color: INK,
                                    lineHeight: 1,
                                    fontWeight: 500,
                                    minWidth: 70,
                                }}
                            >
                                {s.v}
                            </span>
                            <span
                                style={{
                                    fontFamily: SANS,
                                    fontSize: 9.5,
                                    color: INK_SOFT,
                                    letterSpacing: "0.04em",
                                    lineHeight: 1.3,
                                }}
                            >
                                {s.l}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        {/* Toolstack — compact grid */}
        <section style={{ marginBottom: 24 }}>
            <SectionLabel num="03" label="Toolstack" />
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: "10px 28px",
                    fontFamily: SANS,
                    fontSize: 10,
                    color: INK_SOFT,
                    lineHeight: 1.6,
                }}
            >
                {[
                    {
                        h: "AI & Prompt Logic",
                        d: "Claude & Emergent raw APIs · Kaiber AI · ElevenLabs voice cloning · custom audiobook pipelines",
                    },
                    {
                        h: "Content & Edit",
                        d: "Final Cut Pro X · Canva Pro · Meta Glasses Gen 2 multi-format capture · CapCut",
                    },
                    {
                        h: "Performance & Data",
                        d: "Meta CAPI · Stripe webhooks · MailerLite automation · Andromeda framework (Jon Loomer trained)",
                    },
                    {
                        h: "Direct-Response Copy",
                        d: "Gary Halbert (Boron Letters) · Ben Settle email frameworks · broadcast & VSL hooks",
                    },
                    {
                        h: "Engineering",
                        d: "FastAPI server-side · React frontends · server-to-server data loops · webhook architecture",
                    },
                    {
                        h: "Audio Science",
                        d: "432Hz / 963Hz / 396Hz Solfeggio matrices · Scala via Ableton · SSL & TLa Ivory · 5.1 SPL",
                    },
                ].map((t) => (
                    <div key={t.h}>
                        <p
                            style={{
                                fontFamily: SANS,
                                color: INK,
                                fontSize: 10,
                                fontWeight: 700,
                                letterSpacing: "0.08em",
                                textTransform: "uppercase",
                                margin: "0 0 2px",
                            }}
                        >
                            {t.h}
                        </p>
                        <p style={{ margin: 0 }}>{t.d}</p>
                    </div>
                ))}
            </div>
        </section>

        {/* Footer rail */}
        <div
            style={{
                marginTop: "auto",
                paddingTop: 16,
                borderTop: `1px solid ${RULE_SOFT}`,
                display: "flex",
                justifyContent: "space-between",
                fontFamily: SANS,
                fontSize: 8.5,
                color: INK_SOFT,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
            }}
        >
            <span>Tatva K &middot; Resume</span>
            <span style={{ color: BRONZE }}>{portable ? "AI · Content · Sound · Systems" : "For Marisa Peer · More Than Enough"}</span>
            <span>01 / 02</span>
        </div>
    </Page>
);

/* ─────────── PAGE 2 ─────────── */

const Role = ({ company, place, title, dates, lines }) => (
    <div style={{ marginBottom: 10 }}>
        <div
            style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                gap: 12,
                marginBottom: 2,
            }}
        >
            <div>
                <p
                    style={{
                        fontFamily: SERIF,
                        fontSize: 14.5,
                        fontWeight: 500,
                        color: INK,
                        margin: 0,
                        letterSpacing: "-0.01em",
                    }}
                >
                    {company}
                    <span
                        style={{
                            fontFamily: SANS,
                            fontSize: 9,
                            color: INK_SOFT,
                            fontWeight: 400,
                            letterSpacing: "0.16em",
                            textTransform: "uppercase",
                            marginLeft: 10,
                        }}
                    >
                        &mdash; {place}
                    </span>
                </p>
                <p
                    style={{
                        fontFamily: SANS,
                        fontSize: 9.8,
                        color: BRONZE,
                        fontStyle: "italic",
                        margin: "1px 0 0",
                    }}
                >
                    {title}
                </p>
            </div>
            <p
                style={{
                    fontFamily: SANS,
                    fontSize: 9,
                    color: INK_SOFT,
                    margin: 0,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                }}
            >
                {dates}
            </p>
        </div>
        <ul style={{ margin: "3px 0 0", padding: 0 }}>
            {lines.map((l, i) => (
                <Bullet key={i}>{l}</Bullet>
            ))}
        </ul>
    </div>
);

const PageTwo = () => (
    <Page testid="resume-page-2">
        {/* Header strip */}
        <div
            style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                paddingBottom: 10,
                borderBottom: `1px solid ${RULE_SOFT}`,
                marginBottom: 18,
            }}
        >
            <p
                style={{
                    fontFamily: SERIF,
                    fontSize: 22,
                    fontWeight: 500,
                    color: INK,
                    margin: 0,
                    letterSpacing: "-0.01em",
                }}
            >
                Tatva K
                <span
                    style={{
                        fontFamily: SERIF,
                        fontStyle: "italic",
                        color: BRONZE,
                        fontSize: 14,
                        marginLeft: 10,
                    }}
                >
                    &mdash; experience &amp; credentials
                </span>
            </p>
            <Eyebrow>02 / 02</Eyebrow>
        </div>

        {/* Experience */}
        <section style={{ marginBottom: 14 }}>
            <SectionLabel num="04" label="Experience" />

            <Role
                company="The Reset Sanctuary"
                place="Dubai, UAE"
                title="Founder & Chief Systems Architect"
                dates="May 2021 — Present"
                lines={[
                    "Built and operates a full-stack direct-response engine from zero: FastAPI backend + Meta CAPI + Stripe webhooks, running at 100% client data match.",
                    "Automated post-purchase database injection (Stripe → MailerLite segmentation) — zero manual overhead.",
                    "Scaled content factory to 4 long-form audio assets per day, engineered for Insight Timer search algorithms.",
                    "Drives a compounding MailerLite list with 48-hour broadcast cadence — direct-response copy in Gary Halbert / Ben Settle style.",
                ]}
            />

            <Role
                company="Insight Timer"
                place="Global Platform"
                title="Verified Teacher (Premium Catalog)"
                dates="2023 — Present"
                lines={[
                    "Verified Teacher status on the world's #1 meditation platform (30M+ users).",
                    "Featured catalog includes the 599-min hero track 'Eight Hours To Release And Find Peace'.",
                    "Active relationship with platform onboarding & editorial publishing team.",
                ]}
            />

            <Role
                company="Reel Business Inc."
                place="Global"
                title="Founder — AI Audiobook Pioneer"
                dates="2021 — 2022"
                lines={[
                    "Pioneered commercial AI audiobook production before mainstream adoption (pre-ChatGPT era).",
                    "Built end-to-end AI narration + distribution pipeline for international authors including Dale McLaughlin and Mira Brightwood.",
                ]}
            />

            <Role
                company="Moneta Trading Firm"
                place="Dubai, UAE"
                title="Head of Research & Development"
                dates="2020 — 2021"
                lines={[
                    "Led algorithmic market analysis and risk modelling in a proprietary trading environment.",
                    "Translated technical financial data into structured digital education systems.",
                ]}
            />

            <Role
                company="Zabardast Hit 95 FM / Chill Om Studios"
                place="New Delhi"
                title="Celebrity Radio Host & Executive Producer"
                dates="2007 — 2020"
                lines={[
                    "Hosted & produced the network's flagship 9pm–6am show 'Mocktail 95' for 13 consecutive years — an award-winning celebrity media property.",
                    "Designed and built a world-class studio: SSL preamps, TLa Ivory, 5.1 SPL surround.",
                    "Produced 'Rooh' with Tej Gill (200M+ streams). Co-wrote a Juggy D collaboration that ran at iTunes #11.",
                    "TEDx mainstage speaker — sound, cognition, and subconscious rewiring.",
                ]}
            />

            <Role
                company="Chill Om Records & Studios"
                place="India / Global"
                title="Founder & Managing Director"
                dates="2004 — 2007"
                lines={[
                    "Scaled an independent ambient label from zero (early MySpace era) into a Tier-1 commercial deal with Time Music India.",
                    "BBC UK featured (Bobby Friction, Pathaan's Global Musical Rickshaw). Rolling Stone & Verve coverage.",
                    "Performed mainstage at Sunburn Festival 4 consecutive years alongside Armin Van Buuren and Defected Records.",
                ]}
            />
        </section>

        {/* Distribution + Education two-column */}
        <section
            style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 32,
                marginBottom: 8,
            }}
        >
            <div>
                <SectionLabel num="05" label="Distribution" />
                <ul style={{ margin: 0, padding: 0 }}>
                    <Bullet>Instagram personal &mdash; 130K+ followers</Bullet>
                    <Bullet>Good Vibes Entertainment (YouTube) &mdash; 207K+ subs</Bullet>
                    <Bullet>Chill Om YouTube &mdash; 160K+ subs</Bullet>
                    <Bullet>Tatva K Official (YouTube) &mdash; 18K+ subs</Bullet>
                    <Bullet>Total verified catalog streams &mdash; 150M+</Bullet>
                </ul>
            </div>
            <div>
                <SectionLabel num="06" label="Education & Credentials" />
                <ul style={{ margin: 0, padding: 0 }}>
                    <Bullet>
                        Berklee College of Music &mdash; Professional Music &amp; Advanced
                        Production
                    </Bullet>
                    <Bullet>Berklee Music Therapy Masterclass</Bullet>
                    <Bullet>
                        IPHM &mdash; International Practitioners of Holistic Medicine
                    </Bullet>
                    <Bullet>Reiki Level I, II &amp; III Master</Bullet>
                    <Bullet>Insight Timer Verified Teacher</Bullet>
                    <Bullet>TEDx Mainstage Speaker</Bullet>
                </ul>
            </div>
        </section>

        {/* Closing rail */}
        <div
            style={{
                marginTop: "auto",
                paddingTop: 16,
                borderTop: `1px solid ${RULE_SOFT}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontFamily: SANS,
                fontSize: 8.5,
                color: INK_SOFT,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
            }}
        >
            <span>tatvak@theresetsanctuary.org</span>
            <span
                style={{
                    fontFamily: SERIF,
                    fontStyle: "italic",
                    color: BRONZE,
                    fontSize: 11,
                    letterSpacing: "0.02em",
                    textTransform: "none",
                }}
            >
                Available now.
            </span>
            <span>02 / 02</span>
        </div>
    </Page>
);

/* ─────────── ROOT ─────────── */

const Resume = () => {
    return (
        <main
            data-testid="resume-page"
            style={{
                backgroundColor: "#2a2620",
                minHeight: "100vh",
                padding: "40px 16px 80px",
                fontFamily: SANS,
            }}
        >
            {/* Toolbar — hidden in print */}
            <div
                className="resume-toolbar"
                style={{
                    maxWidth: 794,
                    margin: "0 auto 24px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    flexWrap: "wrap",
                }}
            >
                <p
                    style={{
                        color: "#c9a227",
                        fontSize: 11,
                        letterSpacing: "0.3em",
                        textTransform: "uppercase",
                        margin: 0,
                        fontWeight: 600,
                    }}
                >
                    Tatva K &middot; Resume &middot; Print → Save as PDF (A4)
                </p>
                <button
                    type="button"
                    onClick={() => window.print()}
                    data-testid="resume-print-btn"
                    style={{
                        backgroundColor: "#c9a227",
                        color: "#1a1a1a",
                        border: "none",
                        padding: "10px 22px",
                        fontSize: 11,
                        letterSpacing: "0.22em",
                        textTransform: "uppercase",
                        fontWeight: 700,
                        cursor: "pointer",
                        borderRadius: 999,
                    }}
                >
                    Print / Save PDF
                </button>
            </div>

            <PageOne />
            <PageTwo />

            <style>{`
                @page { size: A4; margin: 0; }
                @media print {
                    body, html { background: #ffffff !important; }
                    main[data-testid="resume-page"] { background: #ffffff !important; padding: 0 !important; }
                    .resume-toolbar { display: none !important; }
                    .resume-page {
                        box-shadow: none !important;
                        margin: 0 auto !important;
                        page-break-after: always;
                    }
                    .resume-page:last-child { page-break-after: auto; }
                    nav, footer[data-testid="site-footer"],
                    [data-testid="floating-whatsapp"],
                    [data-testid="cookie-consent-banner"] { display: none !important; }
                }
            `}</style>
        </main>
    );
};

export default Resume;
export { PageOne as ResumePageOne, PageTwo as ResumePageTwo };
